import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, P } from '@maui/elements'
import { GLOBAL_ORG_ID_PARAM, validators } from '@maui-mf/common-auth'
import { navigateToUrl } from 'single-spa'
import { useAuthProvider } from '@maui-mf/common-react'
import { useSearchParam } from 'react-use'
import { Spinner } from './Spinner'
import { isFramed } from '../utils'

export const Signin = () => {
  const { loggedIn, api, claims = {} } = useAuthProvider(false)
  const returnTo = useSearchParam('returnTo')
  const orgParam = useSearchParam(GLOBAL_ORG_ID_PARAM)

  // Get and validate the orgOverride param
  const orgOverride = useMemo(() => {
    // if orgParam is missing or invalid, return
    if (!orgParam || !validators.organizationId(orgParam)) {
      return
    }

    // if we're not logged in, return the param to trigger the SSO login
    if (!loggedIn) {
      return orgParam
    }

    // if the org is already current, return
    if (orgParam === claims.org) {
      console.warn('login org switch attempted for current org:', orgParam)
      return
    }

    // if the org isn't accessible to the user, return
    if (!claims.organizations.some(org => org.uuid === orgParam)) {
      console.warn('login org switch attempted for inaccessible org:', orgParam)
      return
    }

    // finally return the param to trigger the SSO login
    return orgParam
  }, [orgParam, loggedIn, claims])

  // A login will be required if the user is *not* logged in OR a valid org override
  // was determined
  const needsLogin = !loggedIn || !!orgOverride

  // We want to show the "framed" UI if login is necessary and the site is being
  // rendered in an iframe making login unavailable
  const showFramedUI = needsLogin && isFramed()

  // Create a state to track "mode" mostly. If we're showing the framed UI start out
  // in framed mode, otherwise begin in an "init" mode which we'll transition out of
  // into login.
  const [state, setState] = useState({ mode: showFramedUI ? 'framed' : 'init' })
  const { mode } = state

  const signin = useCallback(() => {
    setState({ mode: 'loading' })

    const options = {}

    if (returnTo) {
      options.state = { returnTo }
    }

    if (orgOverride) {
      options.orgOverride = orgOverride
    }

    api.initiateLogin(options).catch(e => {
      console.error(e)
      setState({ mode: 'error', error: e })
    })
  }, [api, orgOverride, returnTo])

  useEffect(() => {
    // if no login is necessary immediately navigate to returnTo
    if (!needsLogin) {
      navigateToUrl(returnTo || '/')
      return
    }

    // We only auto login from the initial state. Any other mode comes with a UI
    // and may or may not present a button to sign in manually.
    if (mode === 'init') {
      signin()
    }
  }, [needsLogin, returnTo, mode, signin])

  // While in init mode, render nothing, avoiding a flash of content before we jump
  // into SSO loading or navigating back to the returnTo
  if (mode === 'init') {
    return null
  }

  return (
    <Box
      flex="1"
      display="flex"
      flexDirection="column"
      width="100%"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
    >
      {mode === 'framed' && <>
        <P m={0}>
          You are accessing the site from inside an iframe where login is unavailable.
        </P>
        <P mb="spaceGapXxLarge">
          Please login in another tab, then click the link below to proceed to your original destination.
        </P>
        <Button
          as="a"
          variant="secondary"
          href={returnTo}
        >
          Continue to site
        </Button>
      </>}

      {mode === 'error' && <>
        <P mt={0} mb="spaceGapXxLarge" color="colorActionDestructive">
          An unexpected error was encountered attempting to log in
        </P>
        <Button
          variant="secondary"
          onClick={signin}
        >
          Try again
        </Button>
      </>}

      {mode === 'loading' && <>
        <Spinner />
        <P>Redirecting to Single Sign On</P>
      </>}
    </Box>
  )
}

export default Signin
